import { useNavigate } from 'react-router-dom'
import type { Patient } from '../../store/patientDetailSlice'

interface PatientListTableProps {
  patients: Patient[]
}

const statusStyles: Record<string, string> = {
  Active: 'bg-accent-subtle-bg text-status-success',
  Inactive: 'bg-[#e8e6e2] text-text-muted',
  Deceased: 'bg-[#2a2a2a] text-white',
}

export function PatientListTable({ patients }: PatientListTableProps) {
  const navigate = useNavigate()

  if (patients.length === 0) {
    return (
      <div data-testid="patients-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No patients found</p>
      </div>
    )
  }

  return (
    <div data-testid="patients-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="pt-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '1.5fr 100px 1fr 100px 1.2fr 100px',
        }}
      >
        <span className="px-2">Patient Name</span>
        <span className="px-2">Species</span>
        <span className="px-2">Breed</span>
        <span className="px-2">Weight</span>
        <span className="px-2">Owner Name</span>
        <span className="px-2">Status</span>
      </div>

      {/* Data rows */}
      {patients.map((patient) => (
        <div
          key={patient.id}
          data-testid={`patient-row-${patient.id}`}
          onClick={() => navigate(`/patients/${patient.id}`)}
          className="pt-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          style={{
            gridTemplateColumns: '1.5fr 100px 1fr 100px 1.2fr 100px',
          }}
        >
          <span className="px-2 font-[500] truncate" data-testid="patient-name">
            {patient.name}
          </span>
          <span className="px-2 text-[12px] text-text-secondary" data-testid="patient-species">
            {patient.species}
          </span>
          <span className="px-2 text-[12px] text-text-muted truncate" data-testid="patient-breed">
            {patient.breed || '—'}
          </span>
          <span className="px-2 text-[12px] text-text-secondary" data-testid="patient-weight">
            {patient.weight != null ? `${parseFloat(String(patient.weight))} ${patient.weight_unit}` : '—'}
          </span>
          <span className="px-2 text-[12px] text-text-secondary truncate" data-testid="patient-owner">
            {patient.owner_name}
          </span>
          <span className="px-2" data-testid="patient-status">
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusStyles[patient.status] || 'bg-[#e8e6e2] text-text-muted'}`}
            >
              {patient.status}
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}
